/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React, {FC} from 'react'
import {Redirect, Switch, Route} from 'react-router-dom'
import {shallowEqual, useSelector} from 'react-redux'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {PrivateRoutes} from './PrivateRoutes'
import {Logout, AuthPage} from '../modules/auth'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {RootState} from '../../setup'
import {CheckInvitationCode} from '../modules/invites/components/CheckInvitationCode'
import {NoCRMAccess} from '../modules/auth/components/NoCRMAccess'
import {ForgotPasswordComplete} from '../modules/auth/components/ForgotPasswordComplete'
import BumpScreenPage from '../modules/cafe-orders/BumpScreenPage'
import {ProjectGuestActivate} from '../modules/project-guest/ProjectGuestActivate'
import {ProjectGuestLogin} from '../modules/project-guest/ProjectGuestLogin'
import {ProjectGuestForgotPassword} from '../modules/project-guest/ProjectGuestForgotPassword'
import {ProjectGuestForgotPasswordComplete} from '../modules/project-guest/ProjectGuestForgotPasswordComplete'
import {ProjectGuestLayout} from '../modules/project-guest/ProjectGuestLayout'
import {ProjectGuestRoutes} from '../modules/project-guest/ProjectGuestRoutes'
import {ProjectRequestApprovalPage} from '../modules/projects/components/ProjectRequestApprovalPage'

const Routes: FC = () => {
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any
  const isAuthorized = !!user

  return (
    <Switch>
      <Route path='/error' component={ErrorsPage} />
      <Route path='/logout' component={Logout} />
      {/* The guest portal signs out through its own path so Logout can tell a guest from a CRM
          user by route alone. The session flags it used to read for that can both be gone by
          the time it runs — see the note in Logout.tsx. Must stay ahead of the /projects/guest
          fork below, which is a non-exact match and would otherwise swallow it. */}
      <Route path='/projects/guest/logout' component={Logout} />
      <Route path='/no-access' component={NoCRMAccess} />
      {/* Ahead of the auth fork: a silent Entra re-sign-in lands here while the persisted user is still set */}
      <Route exact path='/sso-login/:accountId' component={AuthPage} />
      <Route exact path='/invite/accept/:id' component={CheckInvitationCode} />
      <Route exact path='/projects/guest/activate/:code' component={ProjectGuestActivate} />
      {/* Where the Approve link in the manager approval email lands — anonymous, same as the
          guest routes above, the token in the URL is the only credential needed. */}
      <Route
        exact
        path='/projects/requests/approve/:token'
        component={ProjectRequestApprovalPage}
      />
      {/* Skipped once an authenticated guest already has a session — falls through to the
          portal fork below, whose own Switch redirects this exact path to /projects/guest. */}
      {!(isAuthorized && user?.isProjectGuest) && (
        <Route exact path='/projects/guest/login' component={ProjectGuestLogin} />
      )}
      <Route exact path='/projects/guest/forgot-password' component={ProjectGuestForgotPassword} />
      <Route
        exact
        path='/projects/guest/password-reset/complete/:code'
        component={ProjectGuestForgotPasswordComplete}
      />

      {/* Standalone Bump Screen - No Layout */}
      {isAuthorized && <Route exact path='/cafe/bump-screen' component={BumpScreenPage} />}

      {/* Project guest portal — own layout, no CRM chrome (Phase 4.1). A guest must never
          render MasterLayout; a non-guest must never render the portal. */}
      {isAuthorized && (
        <Route path='/projects/guest'>
          {user?.isProjectGuest ? (
            <ProjectGuestLayout>
              <ProjectGuestRoutes />
            </ProjectGuestLayout>
          ) : (
            <Redirect to='/' />
          )}
        </Route>
      )}
      {isAuthorized && user?.isProjectGuest && <Redirect to='/projects/guest' />}

      {!isAuthorized ? (
        /*Render auth page when user at `/auth` and not authorized.*/
        <Route>
          <AuthPage />
        </Route>
      ) : (
        /*Otherwise redirect to root page (`/`)*/
        <Redirect from='/login' to='/' />
      )}

      {!isAuthorized ? (
        /*Redirect to `/auth` when user is not authorized*/
        <Redirect to='/login' />
      ) : (
        <MasterLayout>
          <PrivateRoutes />
        </MasterLayout>
      )}
    </Switch>
  )
}

export {Routes}
